import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/**
 * CSP module (Microsoft NCE licence renewals, ported from NCEasy) — types and hooks.
 * Shapes mirror server/csp/.
 */

/** One subscription row as stored (dates are ISO yyyy-mm-dd). */
export interface CspSubscription {
  customer: string;
  accountManager: string;
  plan: string;
  service: string;
  commitment: string;
  quantity: number;
  startDate: string | null;
  endDate: string | null;
  endDateRaw: string;
  autoRenew: boolean;
  autoRenewRaw: string;
}

export interface CspDataset {
  uploadedAt: string;
  uploadedBy: string;
  fileName: string | null;
  subscriptions: CspSubscription[];
}

export interface CspMe { email: string; name: string; isApprover: boolean; writesEnabled: boolean }

export interface CspProduct {
  atServiceId: number;
  sku: string;
  name: string;
  description: string | null;
  billing: string | null;
  term: string | null;
  productCode: string | null;
  atCost: number | null;
  atSell: number | null;
  margin: number | null;
  synnexSku: string | null;
  synnexCost: number | null;
  synnexSell: number | null;
}

export interface CspMappings {
  clients: Record<string, number | null>;
  products: Record<string, number | null>;
  updatedAt: string | null;
  updatedBy: string | null;
}

export type CspRequestType = 'new' | 'additional' | 'adjustment';
export type CspRequestStatus = 'pending' | 'approved' | 'rejected';

export interface CspWorkflowStep {
  system: 'synnex' | 'autotask';
  status: 'stubbed' | 'planned' | 'executed' | 'needs_selection' | 'error';
  detail: string;
  candidates?: { contractServiceId?: number; contractID: number; contractName?: string; currentQuantity?: number }[];
}

export interface CspRequest {
  id: string;
  customer: string;
  atCompanyId: number | null;
  product: string;
  term: string;
  atServiceId: number | null;
  quantity: number;
  previousQuantity: number | null;
  type: CspRequestType;
  notes: string;
  status: CspRequestStatus;
  requestedBy: string;
  requestedByName: string;
  createdAt: string;
  decidedBy: string | null;
  decidedByName: string | null;
  decidedAt: string | null;
  decisionComment: string | null;
  workflow: { firedAt: string; writesEnabled: boolean; steps: CspWorkflowStep[] } | null;
}

export interface CspRequestInput {
  customer: string;
  atCompanyId?: number | null;
  product: string;
  term?: string;
  atServiceId?: number | null;
  quantity: number;
  previousQuantity?: number | null;
  type: CspRequestType;
  notes?: string;
  autoApprove?: boolean;
}

const K = {
  me: ['csp', 'me'] as const,
  data: ['csp', 'data'] as const,
  products: ['csp', 'products'] as const,
  companies: ['csp', 'companies'] as const,
  mappings: ['csp', 'mappings'] as const,
  requests: ['csp', 'requests'] as const,
};

export function useCspMe() {
  return useQuery({ queryKey: K.me, queryFn: () => api.get<CspMe>('/api/csp/me'), staleTime: 10 * 60_000 });
}

/** The shared dataset. Refetches on focus and hourly, like NCEasy, to pick up other people's uploads. */
export function useCspData() {
  return useQuery({
    queryKey: K.data,
    queryFn: () => api.get<{ dataset: CspDataset | null }>('/api/csp/data').then((r) => r.dataset),
    staleTime: 60_000,
    refetchInterval: 60 * 60_000,
  });
}

export function useUploadCspData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { subscriptions: CspSubscription[]; fileName: string | null }) =>
      api.post<{ dataset: CspDataset }>('/api/csp/data', body).then((r) => r.dataset),
    onSuccess: (dataset) => qc.setQueryData(K.data, dataset),
  });
}

export function useClearCspData() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.del('/api/csp/data'),
    onSuccess: () => qc.setQueryData(K.data, null),
  });
}

export function useCspProducts() {
  return useQuery({
    queryKey: K.products,
    queryFn: () => api.get<{ products: CspProduct[]; synnexConnected: boolean; fetchedAt: string }>('/api/csp/products'),
    staleTime: 5 * 60_000,
  });
}

export function useRefreshCspProducts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.get<{ products: CspProduct[]; synnexConnected: boolean; fetchedAt: string }>('/api/csp/products?fresh=1'),
    onSuccess: (d) => qc.setQueryData(K.products, d),
  });
}

export function useCspCompanies(enabled = true) {
  return useQuery({
    queryKey: K.companies,
    queryFn: () => api.get<{ companies: { id: number; name: string }[] }>('/api/csp/companies').then((r) => r.companies),
    staleTime: 10 * 60_000,
    enabled,
  });
}

export function useCspMappings() {
  return useQuery({ queryKey: K.mappings, queryFn: () => api.get<CspMappings>('/api/csp/mappings'), staleTime: 60_000 });
}

export function useSaveCspMappings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { clients: Record<string, number | null>; products: Record<string, number | null> }) =>
      api.post<CspMappings>('/api/csp/mappings', body),
    onSuccess: (d) => qc.setQueryData(K.mappings, d),
  });
}

export function useCspRequests() {
  return useQuery({
    queryKey: K.requests,
    queryFn: () => api.get<{ canApprove: boolean; me: CspMe; requests: CspRequest[] }>('/api/csp/requests'),
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useCreateCspRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CspRequestInput) => api.post<CspRequest>('/api/csp/requests', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.requests }),
  });
}

export function useDecideCspRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, comment }: { id: string; action: 'approve' | 'reject'; comment?: string }) =>
      api.patch<CspRequest>(`/api/csp/requests/${encodeURIComponent(id)}`, { action, comment: comment ?? '' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: K.requests }),
  });
}
